//@ts-check

import debug from "debug";

const log = debug("slideshow:SlideCycler");

/** @typedef {"next" | "prev"} Direction */
/** @typedef {"scrollHorz" | "fade" | "fadeout" | "none"} Fx */

/**
 * What happens when a step is requested while a transition is still running:
 * - "ignore": the step is dropped.
 * - "jump": the running transition completes at once and the target slide
 *   follows with a quick IMPATIENT_SPEED transition.
 * - "speedUp": the running transition plays on at SPEED_UP_RATE, the requested
 *   one follows at that rate too.
 * @type {"ignore" | "jump" | "speedUp"}
 */
const INTERRUPTION = /** @type {"ignore" | "jump" | "speedUp"} */ ("speedUp");
const SPEED_UP_RATE = 4;
// Transition duration (ms) in impatient mode and for jumps.
const IMPATIENT_SPEED = 300;
// Two user steps within this many ms switch to impatient mode; a pause this long switches back.
const IMPATIENT_COOLDOWN = 2000;
// Pause (ms) without user steps that ends super-impatient mode (no transitions).
const SUPER_IMPATIENT_COOLDOWN = 600;

const EASING = "ease-in-out";

const CURRENT = "slideshow-slide--current";
const OUTGOING = "slideshow-slide--outgoing";
// fadeout: the outgoing slide fades away on top of the incoming one.
const OUTGOING_ON_TOP = "slideshow-slide--outgoing-on-top";

/**
 * Keyframes for the [outgoing, incoming] slide; null means not animated.
 * @param {Fx} fx
 * @param {Direction} direction
 * @returns {[Keyframe[] | null, Keyframe[] | null]}
 */
function keyframesFor(fx, direction) {
  // "prev" mirrors the movement of scrollHorz.
  const sign = direction === "next" ? 1 : -1;
  switch (fx) {
    case "scrollHorz":
      return [
        [
          { transform: "translateX(0)" },
          { transform: `translateX(${-100 * sign}%)` },
        ],
        [
          { transform: `translateX(${100 * sign}%)` },
          { transform: "translateX(0)" },
        ],
      ];
    case "fade":
      return [
        [{ opacity: 1 }, { opacity: 0 }],
        [{ opacity: 0 }, { opacity: 1 }],
      ];
    case "fadeout":
      return [[{ opacity: 1 }, { opacity: 0 }], null];
    default:
      return [null, null];
  }
}

/**
 * One running slide change. The animations use `fill: "forwards"` so their end
 * state holds until the cleanup has swapped the classes; they're cancelled after.
 */
class Transition {
  /**
   * @param {Animation[]} animations
   * @param {object} [options]
   * @param {() => void} [options.onDone]
   */
  constructor(animations, { onDone } = {}) {
    this.animations = animations;
    this.onDone = onDone;
    this.done = false;
    // Cancelling rejects `finished`; the cleanup has run by then anyway.
    this.finished = Promise.all(animations.map((a) => a.finished)).then(
      () => this._complete(),
      () => {},
    );
  }

  // Jumps to the end state and cleans up synchronously, so the caller can
  // lay out the next slide change right away.
  finish() {
    this.animations.forEach((a) => a.finish());
    this._complete();
  }

  /** @param {number} rate */
  speedUp(rate) {
    this.animations.forEach((a) => a.updatePlaybackRate(rate));
  }

  _complete() {
    if (this.done) return;
    this.done = true;
    this.onDone?.();
    this.animations.forEach((a) => a.cancel());
  }
}

/**
 * Stacks the slides on top of each other and animates between them.
 * Replaces jquery.cycle2; has no timer, SlideshowConductor decides when to step.
 */
export class SlideCycler {
  /**
   * @param {HTMLElement[]} slides
   * @param {object} options
   * @param {Fx} options.fx Default effect; a slide's data-fx overrides it.
   * @param {(incoming: HTMLElement) => void} [options.onBefore]
   */
  constructor(slides, { fx, onBefore }) {
    this.slides = slides;
    this.fx = fx;
    // performance.now() of the last user step, ignored ones included.
    this.lastManualStepAt = -Infinity;
    // See SUPER_IMPATIENT_COOLDOWN.
    this.superImpatient = false;
    // Whether the running transition was started in impatient mode.
    this.runningImpatient = false;
    this.onBefore = onBefore;
    // Index of the slide being shown or transitioned to.
    this.index = 0;
    /** @type {Transition | null} */
    this.running = null;
    // Settles once the last started or queued transition is over.
    /** @type {Promise<void>} */
    this.tail = Promise.resolve();

    slides[0]?.classList.add(CURRENT);
  }

  get currentSlide() {
    return this.slides[this.index];
  }

  /**
   * @param {Direction} direction
   * @param {{ speed: number, manual: boolean }} options
   * @returns {number | null} The duration actually used (ms); null if ignored.
   */
  step(direction, { speed, manual }) {
    log("step", direction, { speed, manual });
    const count = this.slides.length;
    if (count < 2) return null;

    // Impatient mode: a user step soon after the previous one plays quickly.
    // Automatic steps are never impatient.
    const now = performance.now();
    const sinceLastManual = now - this.lastManualStepAt;
    const impatient = manual && sinceLastManual < IMPATIENT_COOLDOWN;

    // Super-impatient mode: clicking during an impatient transition disables
    // transitions until the user pauses for SUPER_IMPATIENT_COOLDOWN.
    if (manual && sinceLastManual >= SUPER_IMPATIENT_COOLDOWN) {
      this.superImpatient = false;
    }
    if (manual && this.running && this.runningImpatient) {
      this.superImpatient = true;
    }
    const superImpatient = manual && this.superImpatient;

    if (manual) this.lastManualStepAt = now;

    if (this.running && INTERRUPTION === "ignore" && !superImpatient) {
      log("ignored step during transition");
      return null;
    }

    // speedUp: only the running transition is accelerated; the step itself is
    // dropped, so the index must stay on the slide being transitioned to.
    if (this.running && INTERRUPTION === "speedUp" && !superImpatient) {
      const duration = speed / SPEED_UP_RATE;
      log("speed up", speed, SPEED_UP_RATE, duration);
      this.running.speedUp(SPEED_UP_RATE);
      return duration;
    }

    const from = this.index;
    const to = (from + (direction === "next" ? 1 : -1) + count) % count;
    this.index = to;

    if (superImpatient) {
      this.running?.finish();
      this.tail = this._play(from, to, direction, 0).finished;
      return 0;
    }

    if (!this.running) {
      const duration = impatient ? IMPATIENT_SPEED : speed;
      this.runningImpatient = impatient;
      this.tail = this._play(from, to, direction, duration).finished;
      return duration;
    }

    log("interrupted", INTERRUPTION);
    this.running?.finish();
    this.tail = this._play(from, to, direction, IMPATIENT_SPEED).finished;
    return IMPATIENT_SPEED;
  }

  destroy() {
    this.running?.finish();
    this.running = null;
  }

  /**
   * @param {number} from
   * @param {number} to
   * @param {Direction} direction
   * @param {number} duration 0 swaps the slides without animation.
   * @returns {Transition}
   */
  _play(from, to, direction, duration) {
    const outgoing = this.slides[from];
    const incoming = this.slides[to];
    const fx = /** @type {Fx} */ (incoming.dataset.fx || this.fx);

    this.onBefore?.(incoming);
    outgoing.classList.remove(CURRENT);
    incoming.classList.add(CURRENT);

    const [outFrames, inFrames] = keyframesFor(fx, direction);
    if (duration <= 0 || (!outFrames && !inFrames)) {
      return new Transition([]);
    }

    // Keep the outgoing slide visible (and stacked right) while it animates.
    outgoing.classList.add(OUTGOING);
    outgoing.classList.toggle(OUTGOING_ON_TOP, fx === "fadeout");

    /** @type {KeyframeAnimationOptions} */
    const options = { duration, easing: EASING, fill: "forwards" };
    const animations = [];
    if (outFrames) animations.push(outgoing.animate(outFrames, options));
    if (inFrames) animations.push(incoming.animate(inFrames, options));

    const transition = new Transition(animations, {
      onDone: () => {
        outgoing.classList.remove(OUTGOING, OUTGOING_ON_TOP);
        if (this.running === transition) this.running = null;
      },
    });
    this.running = transition;
    return transition;
  }
}
